'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  CheckSquare, Plus, AlertCircle, X, Phone, Mail,
  Calendar, MessageCircle, Video, Check, Clock, AlertTriangle,
  FileText, Filter, ListChecks, Crosshair, Zap, Activity, ShieldCheck,
  ArrowUpRight, ChevronRight, Briefcase, Info, Edit2, Trash2, Share2, Download
} from 'lucide-react';
import {
  fetchTasks, createTask, updateTask, deleteTask, completeTask, clearError
} from '../../redux/crmSlice';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from '../../utils/dateInput';
import { formatDateTime, dayKey } from '../../utils/formatDate';
import api from '../../services/api';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkActionBar from '../../components/crm/BulkActionBar';
import { downloadCsv } from '../../utils/exportCsv';
import ShareModal from '../../components/crm/ShareModal';
import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';

const TASK_TYPES = ['call', 'email', 'whatsapp', 'meeting', 'note', 'follow_up', 'other', 'technical'];
const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const TASK_STATUS_OPTIONS = [
  { value: 'pending', label: 'PENDING' },
  { value: 'in_progress', label: 'IN PROGRESS' },
  { value: 'completed', label: 'COMPLETED' },
  { value: 'cancelled', label: 'CANCELLED' },
  { value: 'overdue', label: 'OVERDUE' },
];

const TYPE_ICON = {
  call: Phone,
  email: Mail,
  whatsapp: MessageCircle,
  meeting: Video,
  note: FileText,
  technical: Zap,
  other: CheckSquare,
};

function isOverdue(dueDate) {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
}

function fmtDate(d) {
  return formatDateTime(d);
}

export default function TasksPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tasks, loading } = useSelector(state => state.crm);
  const bulk = useBulkSelection();

  const [tab, setTab] = useState('all');
  const [modal, setModal] = useState(false);
  const [sharingTask, setSharingTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'call', priority: 'medium', dueDate: '', description: '', duration: '', visibility: 'team' });

  useEffect(() => {
    dispatch(fetchTasks());
  }, [dispatch]);

  const filtered = useMemo(() => {
    if (tab === 'all') return tasks;
    if (tab === 'today') {
      const todayKey = dayKey(new Date());
      return tasks.filter(t => t.dueDate && dayKey(t.dueDate) === todayKey);
    }
    if (tab === 'overdue') return tasks.filter(t => isOverdue(t.dueDate) && t.status !== 'completed');
    return tasks;
  }, [tasks, tab]);

  const handleComplete = async (id) => {
    const confirmed = await alertService.confirm('Resolve Task?', 'Mark this objective as completed?', 'success', 'Resolve');
    if (confirmed) {
      try {
        await dispatch(completeTask(id)).unwrap();
        alertService.success('Resolved', 'Task completed');
      } catch (err) { alertService.error('Error', 'Action failed'); }
    }
  };

  const closeModal = () => {
    setModal(false);
    setEditingTask(null);
    setForm({ title: '', type: 'call', priority: 'medium', dueDate: '', description: '', duration: '', visibility: 'team' });
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || '',
      type: task.type || 'call',
      priority: task.priority || 'medium',
      dueDate: task.dueDate || '',
      description: task.description || '',
      duration: task.duration ?? '',
      visibility: task.visibility || 'team',
    });
    setModal(true);
  };

  const handleDelete = async (task) => {
    const confirmed = await alertService.confirm('Delete Task?', `Delete "${task.title}"? It will be removed from your lists.`, 'danger', 'Delete');
    if (confirmed) {
      try {
        await dispatch(deleteTask(task._id)).unwrap();
        alertService.success('Deleted', 'Task removed');
      } catch (err) { alertService.error('Error', 'Failed to delete task'); }
    }
  };

  const runBulkAction = async (action, value) => {
    try {
      const res = await api.post('/crm/tasks/bulk', { taskIds: bulk.selectedIds, action, value });
      const count = res.data?.modifiedCount ?? bulk.selectedCount;
      bulk.clear();
      dispatch(fetchTasks());
      alertService.success(action === 'delete' ? 'Deleted' : 'Updated', `${count} task(s) ${action === 'delete' ? 'deleted' : 'updated'}`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk action failed');
    }
  };

  const handleBulkStatusChange = (value) => runBulkAction('change_status', value);
  const handleBulkAssign = (value) => runBulkAction('assign', value);
  const handleBulkDelete = () => runBulkAction('delete');

  const handleExportTasks = async () => {
    try {
      const response = await api.get('/crm/tasks/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export tasks');
    }
  };

  const handleBulkExport = () => {
    const selected = tasks.filter(t => bulk.selected.has(t._id));
    downloadCsv(
      'tasks-selected.csv',
      ['Title', 'Type', 'Priority', 'Status', 'DueDate', 'AssignedTo'],
      selected.map(t => [t.title, t.type, t.priority, t.status, t.dueDate, t.assignedTo || ''])
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      if (editingTask) {
        await dispatch(updateTask({ id: editingTask._id, data: form })).unwrap();
        alertService.success('Updated', 'Task updated');
      } else {
        await dispatch(createTask(form)).unwrap();
        alertService.success('Success', 'New task created');
      }
      closeModal();
    } catch (err) {
      alertService.error('Error', editingTask ? 'Failed to update task' : 'Failed to save task');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading && tasks.length === 0) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader title="Tasks" subtitle={<>{tasks.filter(t => t.status !== 'completed').length} Pending Tasks</>} icon={CheckSquare} actions={<>
          <button onClick={handleExportTasks} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2] transition-all flex items-center gap-2">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={() => setModal(true)} className="h-9 px-4 bg-[#0176D3] text-white rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2">
            <Plus size={14} /> New Task
          </button>
      </>} />

      {/* KPI Matrix */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Total Active</p>
          <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">{tasks.filter(t => t.status !== 'completed').length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#BA0517]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Overdue</p>
          <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">{tasks.filter(t => isOverdue(t.dueDate) && t.status !== 'completed').length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider mb-1">Completion Rate</p>
          <h3 className="text-2xl font-bold text-[#2E2E2E] tabular-nums">
            {tasks.length ? Math.round((tasks.filter(t => t.status === 'completed').length / tasks.length) * 100) : 0}%
          </h3>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded-md w-fit max-w-full overflow-x-auto">
        {[
          { id: 'all', label: 'All Tasks' },
          { id: 'today', label: 'Due Today' },
          { id: 'overdue', label: 'Overdue' }
        ].map(t => (
          <button
            key={t.id}
            className={cn(
              "px-4 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-all",
              tab === t.id ? "bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]" : "text-[#706E6B] hover:text-[#0176D3]"
            )}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Bulk selection controls */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={bulk.isAllSelected(filtered.map(t => t._id))}
            onChange={() => bulk.toggleAll(filtered.map(t => t._id))}
            className="w-4 h-4 accent-[#0176D3] cursor-pointer"
          />
          <span className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wider">Select all visible tasks</span>
        </label>
      </div>
      <BulkActionBar
        selectedCount={bulk.selectedCount}
        onClear={bulk.clear}
        statusOptions={TASK_STATUS_OPTIONS}
        onStatusChange={handleBulkStatusChange}
        onAssign={handleBulkAssign}
        assignPlaceholder="Assign to..."
        onDelete={handleBulkDelete}
        onExport={handleBulkExport}
        entityLabel="task"
      />

      {/* Tasks List */}
      <div className="space-y-4">
        {filtered.length > 0 ? filtered.map(task => {
          const Icon = TYPE_ICON[task.type] || CheckSquare;
          const overdue = isOverdue(task.dueDate) && task.status !== 'completed';
          const done = task.status === 'completed';

          return (
            <div key={task._id} className={cn(
              "sf-card p-4 flex items-center gap-4 hover:border-[#0176D3] transition-all group",
              bulk.selected.has(task._id) && "ring-2 ring-[#0176D3]",
              done && "opacity-60 grayscale"
            )}>
              <input
                type="checkbox"
                checked={bulk.selected.has(task._id)}
                onChange={() => bulk.toggle(task._id)}
                aria-label={`Select ${task.title}`}
                className="w-4 h-4 accent-[#0176D3] cursor-pointer shrink-0"
              />
              <div className="w-10 h-10 rounded border border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-center shrink-0">
                <Icon size={18} className="text-[#0176D3]" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h4
                    onClick={() => navigate(`/dashboard/crm/tasks/${task._id}`)}
                    className={cn("text-[14px] font-bold text-[#2E2E2E] hover:text-[#0176D3] hover:underline cursor-pointer", done && "line-through")}
                  >
                    {task.title}
                  </h4>
                  <span className={cn(
                    "sf-badge",
                    task.priority === 'urgent' ? 'sf-badge-error' :
                    task.priority === 'high' ? 'sf-badge-warning' : 'sf-badge-neutral'
                  )}>
                    {task.priority || 'medium'}
                  </span>
                  {task.sourceTicketId && (
                    <span className="sf-badge sf-badge-manager" title={task.sourceTicketId.subject}>
                      From Ticket #{task.sourceTicketId.ticketNumber}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-[11px] text-[#706E6B]">
                  <span className={cn("flex items-center gap-1 font-bold", overdue && "text-[#BA0517]")}>
                    <Clock size={12} /> {fmtDate(task.dueDate)} {overdue && '[OVERDUE]'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Activity size={12} /> {task.type || 'Standard'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {!done ? (
                  <button 
                    onClick={() => handleComplete(task._id)}
                    className="h-8 px-3 border border-[#2E844A] text-[#2E844A] rounded text-[11px] font-semibold hover:bg-[#EBF3ED] transition-all flex items-center gap-2"
                  >
                    <Check size={14} /> Resolve
                  </button>
                ) : (
                  <span className="sf-badge sf-badge-success flex items-center gap-1.5">
                    <ShieldCheck size={12} /> Closed
                  </span>
                )}
                <button onClick={() => openEdit(task)} className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors" title="Edit"><Edit2 size={14} /></button>
                <button onClick={() => setSharingTask(task)} className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors" title="Share"><Share2 size={14} /></button>
                <button onClick={() => handleDelete(task)} className="p-2 text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors" title="Delete"><Trash2 size={14} /></button>
              </div>
            </div>
          );
        }) : (
          <div className="sf-card py-24 text-center border-dashed border-2 opacity-50">
            <CheckSquare className="mx-auto mb-4 opacity-20" size={48} />
            <p className="text-sm font-bold text-[#706E6B]">No objectives found</p>
          </div>
        )}
      </div>

      {/* Task Modal */}
      {modal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg z-10 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editingTask ? 'Edit Task' : 'New Task'}</h3>
              <button onClick={closeModal} className="text-[#706E6B] hover:text-[#2E2E2E]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Subject</label>
                <input required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Priority</label>
                  <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all">
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Type</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all">
                    {TASK_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Visibility</label>
                <select value={form.visibility} onChange={e => setForm(p => ({ ...p, visibility: e.target.value }))} className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all">
                  <option value="team">Team — everyone can see this</option>
                  <option value="private">Private — only owner and shared users</option>
                </select>
              </div>

              {(form.type === 'call' || form.type === 'meeting') && (
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Duration (minutes)</label>
                  <input type="number" min="0" value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))} placeholder="e.g. 30" className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all" />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Resolution Date</label>
                <input type="datetime-local" required value={toDatetimeLocalValue(form.dueDate)} onChange={e => setForm(p => ({ ...p, dueDate: fromDatetimeLocalValue(e.target.value) }))} className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] transition-all" />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Notes</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full min-h-[100px] p-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] focus:bg-white focus:border-[#0176D3] outline-none transition-all resize-none" />
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={closeModal} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ShareModal
        isOpen={!!sharingTask}
        onClose={() => setSharingTask(null)}
        resourceType="task"
        resourceId={sharingTask?._id}
        resourceLabel="Task"
      />
    </div>
  );
}
